import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
}

const CHEVRON =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'><path d='M2.5 4.5L6 8L9.5 4.5' stroke='%236d665f' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>\")";

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, id, className, children, ...rest },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const errorId = `${selectId}-error`;
  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <label htmlFor={selectId} className="text-meta text-ink-1 font-medium">
          {label}
        </label>
      ) : null}
      <select
        ref={ref}
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        style={{
          backgroundImage: CHEVRON,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 10px center',
        }}
        className={cn(
          'h-9 pl-3 pr-8 text-ui bg-paper-0 text-ink-0 border rounded-token-md appearance-none',
          'focus:outline-none focus-visible:shadow-focus',
          'disabled:bg-paper-1 disabled:text-ink-3 disabled:cursor-not-allowed',
          'transition-colors duration-2 ease-brand',
          error
            ? 'border-status-err focus-visible:border-status-err'
            : 'border-line-2 focus-visible:border-primary',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      {error ? (
        <p id={errorId} className="text-meta text-status-err">
          {error}
        </p>
      ) : null}
    </div>
  );
});
